import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, Undo2, ChevronLeft, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { formatPrice, formatQty } from '@/lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const REASONS = ['Wrong size', 'Defective', 'Not needed', 'Damaged in transit', 'Changed mind', 'Other'];

const Returns = () => {
  const [returns, setReturns] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [eligibleItems, setEligibleItems] = useState([]);
  const [selectedQty, setSelectedQty] = useState({}); // {order_item_id: qty}
  const [form, setForm] = useState({
    customer_id: '',
    reason: '',
    reason_notes: '',
    return_date: new Date().toISOString().split('T')[0],
    stock_action: 'restock',
    refund_mode: 'cash',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [retRes, custRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/returns`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/customers`, { withCredentials: true }),
      ]);
      setReturns(retRes.data);
      setCustomers(custRes.data);
    } catch (e) {
      toast.error('Failed to load returns');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = () => {
    setStep(1);
    setEligibleItems([]);
    setSelectedQty({});
    setForm({
      customer_id: '',
      reason: '',
      reason_notes: '',
      return_date: new Date().toISOString().split('T')[0],
      stock_action: 'restock',
      refund_mode: 'cash',
    });
    setDialogOpen(true);
  };

  const onCustomerSelected = async (customer_id) => {
    setForm({ ...form, customer_id });
    setEligibleItems([]);
    setSelectedQty({});
    try {
      const res = await axios.get(`${BACKEND_URL}/api/returns/eligible-items/${customer_id}`, { withCredentials: true });
      setEligibleItems(res.data);
      if (res.data.length === 0) {
        toast.info('No delivered items eligible for return for this customer');
      }
    } catch (e) {
      toast.error('Failed to load eligible items');
    }
  };

  const setItemQty = (oid, val) => {
    const item = eligibleItems.find(i => i.order_item_id === oid);
    if (!item) return;
    const n = parseFloat(val);
    if (isNaN(n) || n <= 0) {
      const next = { ...selectedQty };
      delete next[oid];
      setSelectedQty(next);
      return;
    }
    if (n > item.eligible_qty) {
      toast.error(`Max ${item.eligible_qty} can be returned`);
      return;
    }
    setSelectedQty({ ...selectedQty, [oid]: n });
  };

  const selectedItems = eligibleItems.filter(i => (selectedQty[i.order_item_id] || 0) > 0);
  const previewValue = selectedItems.reduce((s, i) => s + (i.unit_price * selectedQty[i.order_item_id]), 0);

  const submit = async () => {
    if (!form.customer_id) return toast.error('Select a customer');
    if (!form.reason) return toast.error('Select a reason');
    if (selectedItems.length === 0) return toast.error('Select at least one item');
    setSubmitting(true);
    try {
      const items = selectedItems.map(i => ({ order_item_id: i.order_item_id, qty: selectedQty[i.order_item_id] }));
      // All selected items must come from a single project for the project_id field
      const projectIds = Array.from(new Set(selectedItems.map(i => i.project_id)));
      if (projectIds.length > 1) {
        toast.error('Items selected belong to multiple projects. Please return one project at a time.');
        setSubmitting(false);
        return;
      }
      await axios.post(`${BACKEND_URL}/api/returns`, {
        customer_id: form.customer_id,
        project_id: projectIds[0],
        items,
        reason: form.reason,
        reason_notes: form.reason_notes,
        return_date: form.return_date,
        stock_action: form.stock_action,
        refund_mode: form.refund_mode,
      }, { withCredentials: true });
      toast.success('Return processed successfully');
      setDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record return');
    } finally {
      setSubmitting(false);
    }
  };

  const refundLabel = (mode) => ({
    cash: 'Cash refund',
    credit_to_balance: 'Credit to balance',
    no_refund: 'No refund',
  })[mode] || mode;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-stone-900 flex items-center gap-2"><Undo2 className="w-5 h-5" /> Returns</h1>
          <p className="text-xs text-stone-500 mt-0.5">Record customer returns. Refunds and inventory restocking are handled automatically.</p>
        </div>
        <Button data-testid="new-return-btn" onClick={openDialog} className="bg-amber-700 hover:bg-amber-800 text-white">
          <Plus className="w-4 h-4 mr-1" /> New Return
        </Button>
      </div>

      <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              <th className="text-left p-3 font-semibold text-stone-600">DATE</th>
              <th className="text-left p-3 font-semibold text-stone-600">RETURN ID</th>
              <th className="text-left p-3 font-semibold text-stone-600">CUSTOMER</th>
              <th className="text-left p-3 font-semibold text-stone-600">PROJECT</th>
              <th className="text-left p-3 font-semibold text-stone-600">ITEMS</th>
              <th className="text-right p-3 font-semibold text-stone-600">VALUE</th>
              <th className="text-left p-3 font-semibold text-stone-600">STOCK</th>
              <th className="text-left p-3 font-semibold text-stone-600">REFUND</th>
              <th className="text-left p-3 font-semibold text-stone-600">REASON</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="p-6 text-center text-stone-400">Loading…</td></tr>
            ) : returns.length === 0 ? (
              <tr><td colSpan={9} className="p-6 text-center text-stone-400">No returns yet. Click "New Return" to record your first one.</td></tr>
            ) : returns.map(r => (
              <tr key={r.return_id} data-testid={`return-row-${r.return_id}`} className="border-b border-stone-100 hover:bg-stone-50">
                <td className="p-3 tabular-nums text-stone-700">{r.return_date}</td>
                <td className="p-3 font-mono text-[11px] text-stone-500">{r.return_id}</td>
                <td className="p-3 text-stone-900">{r.customer_name}</td>
                <td className="p-3 text-stone-700">{r.project_name}</td>
                <td className="p-3 text-stone-600">{r.items?.length || 0}</td>
                <td className="p-3 text-right tabular-nums font-semibold text-stone-900">₹{formatPrice(r.total_value)}</td>
                <td className="p-3">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${r.stock_action === 'restock' ? 'bg-lime-100 text-lime-800' : 'bg-amber-100 text-amber-800'}`}>
                    {r.stock_action}
                  </span>
                </td>
                <td className="p-3 text-stone-700">{refundLabel(r.refund_mode)}</td>
                <td className="p-3 text-stone-600">{r.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {step > 1 && <button onClick={() => setStep(step - 1)} className="text-stone-500 hover:text-stone-800"><ChevronLeft className="w-4 h-4" /></button>}
              New Return — Step {step} of 3
            </DialogTitle>
          </DialogHeader>

          {step === 1 && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Customer *</Label>
                <Select value={form.customer_id} onValueChange={onCustomerSelected}>
                  <SelectTrigger data-testid="return-customer-select" className="border-stone-200 h-9"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>
                    {customers.map(c => <SelectItem key={c.customer_id} value={c.customer_id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.customer_id && eligibleItems.length === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>No delivered items found for this customer that can be returned. Returns are only possible for items already marked as Delivered.</span>
                </div>
              )}
              {eligibleItems.length > 0 && (
                <div>
                  <Label className="text-xs mb-2 block">Select Items & Quantities to Return</Label>
                  <div className="border border-stone-200 rounded-md overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-stone-50 border-b border-stone-200">
                        <tr>
                          <th className="text-left p-2 font-semibold text-stone-600">PROJECT</th>
                          <th className="text-left p-2 font-semibold text-stone-600">PRODUCT</th>
                          <th className="text-right p-2 font-semibold text-stone-600">UNIT ₹</th>
                          <th className="text-right p-2 font-semibold text-stone-600">DELIVERED</th>
                          <th className="text-right p-2 font-semibold text-stone-600">ELIGIBLE</th>
                          <th className="text-right p-2 font-semibold text-stone-600 w-24">RETURN QTY</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eligibleItems.map(it => (
                          <tr key={it.order_item_id} className="border-b border-stone-100">
                            <td className="p-2 text-stone-700">{it.project_name}</td>
                            <td className="p-2 text-stone-900">{it.product_name} <span className="text-stone-500">— {it.product_brand} {it.product_size}</span></td>
                            <td className="p-2 text-right tabular-nums">₹{formatPrice(it.unit_price)}</td>
                            <td className="p-2 text-right tabular-nums">{formatQty(it.delivered_qty)}</td>
                            <td className="p-2 text-right tabular-nums font-semibold text-stone-900">{formatQty(it.eligible_qty)}</td>
                            <td className="p-2">
                              <Input
                                data-testid={`return-qty-${it.order_item_id}`}
                                type="number"
                                step="0.01"
                                min="0"
                                max={it.eligible_qty}
                                value={selectedQty[it.order_item_id] || ''}
                                onChange={(e) => setItemQty(it.order_item_id, e.target.value)}
                                className="h-7 text-xs text-right tabular-nums"
                                placeholder="0"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <div className="flex justify-between items-center pt-2">
                <span className="text-xs text-stone-500">{selectedItems.length} item(s) selected · ₹{formatPrice(previewValue)} total</span>
                <Button data-testid="step1-next-btn" disabled={selectedItems.length === 0} onClick={() => setStep(2)} className="bg-amber-700 hover:bg-amber-800 text-white">
                  Next →
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Reason *</Label>
                  <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                    <SelectTrigger data-testid="return-reason-select" className="border-stone-200 h-9"><SelectValue placeholder="Select reason" /></SelectTrigger>
                    <SelectContent>
                      {REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Return Date *</Label>
                  <Input type="date" value={form.return_date} onChange={(e) => setForm({ ...form, return_date: e.target.value })} className="border-stone-200 h-9" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Input value={form.reason_notes} onChange={(e) => setForm({ ...form, reason_notes: e.target.value })} placeholder="Optional details" className="border-stone-200 h-9" />
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div>
                  <Label className="text-xs mb-1 block">Stock Action *</Label>
                  <div className="space-y-1">
                    {[
                      { v: 'restock', label: 'Restock to inventory', help: 'Returned items go back to sellable stock' },
                      { v: 'scrap', label: 'Scrap / write off', help: 'Items damaged, cannot be resold (cost is lost)' },
                    ].map(opt => (
                      <label key={opt.v} data-testid={`stock-${opt.v}-radio`} className={`flex gap-2 items-start p-2 rounded border cursor-pointer text-xs ${form.stock_action === opt.v ? 'border-amber-500 bg-amber-50' : 'border-stone-200 hover:bg-stone-50'}`}>
                        <input type="radio" checked={form.stock_action === opt.v} onChange={() => setForm({ ...form, stock_action: opt.v })} className="mt-0.5" />
                        <div>
                          <div className="font-semibold text-stone-800">{opt.label}</div>
                          <div className="text-[10px] text-stone-500">{opt.help}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Refund Mode *</Label>
                  <div className="space-y-1">
                    {[
                      { v: 'cash', label: 'Cash refund', help: 'Money paid back to customer now' },
                      { v: 'credit_to_balance', label: 'Credit to balance', help: 'Customer holds credit for future orders' },
                      { v: 'no_refund', label: 'No refund (courtesy)', help: 'Inventory restored, no money moves' },
                    ].map(opt => (
                      <label key={opt.v} data-testid={`refund-${opt.v}-radio`} className={`flex gap-2 items-start p-2 rounded border cursor-pointer text-xs ${form.refund_mode === opt.v ? 'border-amber-500 bg-amber-50' : 'border-stone-200 hover:bg-stone-50'}`}>
                        <input type="radio" checked={form.refund_mode === opt.v} onChange={() => setForm({ ...form, refund_mode: opt.v })} className="mt-0.5" />
                        <div>
                          <div className="font-semibold text-stone-800">{opt.label}</div>
                          <div className="text-[10px] text-stone-500">{opt.help}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <Button data-testid="step2-next-btn" disabled={!form.reason} onClick={() => setStep(3)} className="bg-amber-700 hover:bg-amber-800 text-white">
                  Review →
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <div className="bg-stone-50 border border-stone-200 rounded-md p-3 space-y-2 text-xs">
                <div className="flex justify-between"><span className="text-stone-500">Customer</span><span className="font-semibold text-stone-900">{customers.find(c => c.customer_id === form.customer_id)?.name}</span></div>
                <div className="flex justify-between"><span className="text-stone-500">Return Date</span><span className="font-semibold text-stone-900">{form.return_date}</span></div>
                <div className="flex justify-between"><span className="text-stone-500">Reason</span><span className="font-semibold text-stone-900">{form.reason}{form.reason_notes ? ` — ${form.reason_notes}` : ''}</span></div>
                <div className="flex justify-between"><span className="text-stone-500">Stock Action</span><span className="font-semibold text-stone-900 capitalize">{form.stock_action}</span></div>
                <div className="flex justify-between"><span className="text-stone-500">Refund Mode</span><span className="font-semibold text-stone-900">{refundLabel(form.refund_mode)}</span></div>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Items</Label>
                <table className="w-full text-xs border border-stone-200 rounded-md overflow-hidden">
                  <thead className="bg-stone-50 border-b border-stone-200">
                    <tr>
                      <th className="text-left p-2 font-semibold text-stone-600">PRODUCT</th>
                      <th className="text-right p-2 font-semibold text-stone-600">QTY</th>
                      <th className="text-right p-2 font-semibold text-stone-600">UNIT ₹</th>
                      <th className="text-right p-2 font-semibold text-stone-600">LINE ₹</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedItems.map(it => {
                      const q = selectedQty[it.order_item_id];
                      return (
                        <tr key={it.order_item_id} className="border-b border-stone-100 last:border-0">
                          <td className="p-2 text-stone-900">{it.product_name}</td>
                          <td className="p-2 text-right tabular-nums">{formatQty(q)}</td>
                          <td className="p-2 text-right tabular-nums">₹{formatPrice(it.unit_price)}</td>
                          <td className="p-2 text-right tabular-nums font-semibold">₹{formatPrice(it.unit_price * q)}</td>
                        </tr>
                      );
                    })}
                    <tr className="bg-stone-50 font-bold">
                      <td className="p-2 text-stone-700" colSpan={3}>Total Return Value</td>
                      <td className="p-2 text-right tabular-nums">₹{formatPrice(previewValue)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-[11px] text-blue-900">
                <strong>What happens when you confirm:</strong>
                <ul className="mt-1 ml-4 list-disc space-y-0.5">
                  <li>Inventory will be {form.stock_action === 'restock' ? `increased by ${selectedItems.length} product line(s)` : 'NOT changed (items will be scrapped)'}</li>
                  <li>{form.refund_mode === 'cash' && `Customer's outstanding stays the same; ₹${formatPrice(previewValue)} cash will be refunded`}</li>
                  <li>{form.refund_mode === 'credit_to_balance' && `Customer's outstanding will reduce by ₹${formatPrice(previewValue)} (they'll have credit for future orders)`}</li>
                  <li>{form.refund_mode === 'no_refund' && 'No money movement. Customer balance unchanged.'}</li>
                  <li>WhatsApp confirmation will be sent to admin (and customer if opted in)</li>
                </ul>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
                <Button data-testid="confirm-return-btn" disabled={submitting} onClick={submit} className="bg-amber-700 hover:bg-amber-800 text-white">
                  {submitting ? 'Processing…' : 'Confirm & Process Return'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Returns;
